import React, { useState, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { FiFilter, FiX, FiRotateCcw, FiMapPin, FiLayers } from "react-icons/fi";
import "../../../components/common/saas/SaaSUIToolkit.css";

export const PostFilter = ({ posts = [], onApply }) => {
  const [filters, setFilters] = useState({
    state: "",
    city: "",
    service: "",
    area: "",
  });
  const [show, setShow] = useState(false);

  // Derive unique states from available posts
  const availableStates = useMemo(() => {
    return [...new Set(posts.map((p) => p.state_name || p.state).filter(Boolean))].sort();
  }, [posts]);

  // Derive dependent cities based on selected state
  const availableCities = useMemo(() => {
    let subset = posts;
    if (filters.state) {
      subset = subset.filter((p) => (p.state_name || p.state) === filters.state);
    }
    return [...new Set(subset.map((p) => p.city_name || p.city).filter(Boolean))].sort();
  }, [posts, filters.state]);

  // Derive dependent areas based on selected city
  const availableAreas = useMemo(() => {
    let subset = posts;
    if (filters.city) {
      subset = subset.filter((p) => (p.city_name || p.city) === filters.city);
    }
    return [...new Set(subset.map((p) => p.area_name || p.area).filter(Boolean))].sort();
  }, [posts, filters.city]);

  // Derive available services
  const availableServices = useMemo(() => {
    return [...new Set(posts.map((p) => p.service || p.service_name).filter(Boolean))].sort();
  }, [posts]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const handleChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "state") {
        next.city = "";
        next.area = "";
      }
      if (key === "city") {
        next.area = "";
      }
      return next;
    });
  };

  const handleApply = () => {
    onApply(filters);
    setShow(false);
  };

  const handleReset = () => {
    const emptyFilters = { state: "", city: "", service: "", area: "" };
    setFilters(emptyFilters);
    onApply(emptyFilters);
  };

  return (
    <>
      <button
        type="button"
        className={`saas-btn ${activeFilterCount > 0 ? "saas-btn--primary" : "saas-btn--secondary"}`}
        onClick={() => setShow(true)}
      >
        <FiFilter size={15} />
        <span>Filter</span>
        {activeFilterCount > 0 && (
          <span className="badge bg-white text-primary rounded-pill ms-1" style={{ fontSize: "0.7rem" }}>
            {activeFilterCount}
          </span>
        )}
      </button>

      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        dialogClassName="saas-modal-dialog"
      >
        <div className="p-3 p-md-4 bg-white rounded-3">
          <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
            <div className="d-flex align-items-center gap-2">
              <div className="saas-stat-card__icon-wrap--blue p-2 rounded-2" style={{ width: 34, height: 34 }}>
                <FiFilter size={16} />
              </div>
              <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: "1.1rem" }}>
                Filter Posts & Listings
              </h4>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light border-0 rounded-circle d-flex p-1"
              onClick={() => setShow(false)}
            >
              <FiX size={18} />
            </button>
          </div>

          <div className="saas-filter-body d-flex flex-column gap-3">
            {/* Location Section */}
            <div>
              <div className="d-flex align-items-center gap-2 text-muted mb-2" style={{ fontSize: "0.8125rem", fontWeight: 700, textTransform: "uppercase" }}>
                <FiMapPin size={14} className="text-primary" />
                <span>Location Filter</span>
              </div>
              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <label className="form-label" style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                    State
                  </label>
                  <select
                    className="form-select"
                    style={{ fontSize: "0.875rem", borderRadius: 8 }}
                    value={filters.state}
                    onChange={(e) => handleChange("state", e.target.value)}
                  >
                    <option value="">All States</option>
                    {availableStates.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                    City
                  </label>
                  <select
                    className="form-select"
                    style={{ fontSize: "0.875rem", borderRadius: 8 }}
                    value={filters.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                  >
                    <option value="">All Cities</option>
                    {availableCities.map((ct) => (
                      <option key={ct} value={ct}>
                        {ct}
                      </option>
                    ))}
                  </select>
                </div>

                {availableAreas.length > 0 && (
                  <div className="col-12">
                    <label className="form-label" style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                      Area
                    </label>
                    <select
                      className="form-select"
                      style={{ fontSize: "0.875rem", borderRadius: 8 }}
                      value={filters.area}
                      onChange={(e) => handleChange("area", e.target.value)}
                    >
                      <option value="">All Areas</option>
                      {availableAreas.map((ar) => (
                        <option key={ar} value={ar}>
                          {ar}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Service Domain Section */}
            <div className="pt-2 border-top">
              <div className="d-flex align-items-center gap-2 text-muted mb-2" style={{ fontSize: "0.8125rem", fontWeight: 700, textTransform: "uppercase" }}>
                <FiLayers size={14} className="text-primary" />
                <span>Service Category</span>
              </div>
              <select
                className="form-select"
                style={{ fontSize: "0.875rem", borderRadius: 8 }}
                value={filters.service}
                onChange={(e) => handleChange("service", e.target.value)}
              >
                <option value="">All Services</option>
                {availableServices.map((srv) => (
                  <option key={srv} value={srv}>
                    {srv}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between pt-4 border-top mt-3">
            <button
              type="button"
              className="saas-btn saas-btn--secondary"
              onClick={handleReset}
              disabled={activeFilterCount === 0}
            >
              <FiRotateCcw size={14} />
              <span>Reset</span>
            </button>
            <button
              type="button"
              className="saas-btn saas-btn--primary"
              onClick={handleApply}
            >
              <span>Apply Filters</span>
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PostFilter;
